import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { accountApi, formatCurrency } from '../services/api.js';
import AddTransactionModal from './AddTransactionModal.js';
import EditTransactionModal from './EditTransactionModal.js';
import EditValuationModal from './EditValuationModal.js';
import EditAccountModal from './EditAccountModal.js';
import DeleteAccountDialog from './DeleteAccountDialog.js';
import DeactivateAccountDialog from './DeactivateAccountDialog.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const AccountDetailContainer = styled.div`
  .account-header {
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 30px;
    
    h2 {
      margin-top: 0;
      color: #333;
      margin-bottom: 0; /* Remove bottom margin to align better in flex container */
    }

    .header-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 15px;
    }

    .header-actions {
      display: flex;
      gap: 10px;
    }
    
    .account-info {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      margin-top: 15px;
    }
    
    .info-item {
      flex: 1;
      min-width: 200px;
      
      .label {
        font-weight: 600;
        color: #666;
        margin-bottom: 5px;
      }
      
      .value {
        font-size: 1.2rem;
        font-weight: 700;
      }
    }
  }
  
  .tabs-container {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .tabs-header {
    display: flex;
    border-bottom: 2px solid #e0e0e0;
    background-color: #f8f9fa;
    
    .tab-button {
      flex: 1;
      padding: 15px 20px;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      font-weight: 600;
      color: #666;
      transition: all 0.3s;
      border-bottom: 3px solid transparent;
      
      &:hover {
        background-color: #e9ecef;
        color: #333;
      }
      
      &.active {
        color: #007bff;
        border-bottom-color: #007bff;
        background-color: white;
      }
    }
  }
  
  .tab-content {
    padding: 20px;
  }
  
  .transactions-table {
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow-x: auto;
    
    .transactions-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      
      h3 {
        margin: 0;
      }
    }
  }
  
  .chart-container {
    padding: 20px;
    min-height: 400px;
  }
  
  .action-buttons {
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    /* .action-buttons class might be unused now, but keeping generic styles just in case, 
       or we can repurpose .action-button styles which are effectively global in this scope */
  }
  
  .action-button {
    padding: 10px 15px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
      background-color: #0056b3;
    }
    
    &.edit {
      background-color: #28a745;
      
      &:hover {
        background-color: #218838;
      }
    }
    
    &.delete {
      background-color: #dc3545;
      
      &:hover {
        background-color: #c82333;
      }
    }
    
    &.deactivate {
      background-color: #ffc107;
      color: #212529;
      
      &:hover {
        background-color: #e0a800;
      }
    }
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    
    th, td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid #ddd;
    }
    
    th {
      background-color: #f8f9fa;
      font-weight: 600;
    }
    
    tr:hover {
      background-color: #f5f5f5;
    }
    
    tr[style*="cursor: pointer"]:hover {
      background-color: #e3f2fd;
      transform: scale(1.01);
      transition: all 0.2s;
    }
    
    .income {
      color: #28a745;
    }
    
    .expense {
      color: #dc3545;
    }
    
    .transfer {
      background-color: #fffacd;
    }
    
    .valuation {
      background-color: #e7f3ff;
    }
    
    .valuation-type {
      color: #0066cc;
      font-weight: 600;
    }
  }
  
  // Investment account table (4 columns)
  .investment-transactions-table {
    // Set column widths for 4 columns
    th:nth-child(1), td:nth-child(1) { // 타입
      width: 5%;
    }
    th:nth-child(2), td:nth-child(2) { // 금액
      width: 5%;
    }
    th:nth-child(3), td:nth-child(3) { // 메모
      width: 40%;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    th:nth-child(4), td:nth-child(4) { // 날짜
      width: 10%;
    }
  }
  
  // Cash account table (5 columns)
  .cash-transactions-table {
    // Set column widths for 5 columns with memo column being the widest
    th:nth-child(1), td:nth-child(1) { // 타입
      width: 5%;
    }
    th:nth-child(2), td:nth-child(2) { // 금액
      width: 10%;
    }
    th:nth-child(3), td:nth-child(3) { // 카테고리
      width: 10%;
    }
    th:nth-child(4), td:nth-child(4) { // 메모
      width: 40%;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    th:nth-child(5), td:nth-child(5) { // 날짜
      width: 10%;
    }
  }
  
  @media (max-width: 768px) {
    .account-header {
      padding: 15px;
    }
    
    .transactions-table {
      padding: 15px;
    }
    
    .action-buttons {
      flex-direction: column;
    }
    
    .action-button {
      width: 100%;
      margin-bottom: 10px;
    }
    
    table {
      font-size: 0.9rem;
      
      th, td {
        padding: 8px 10px;
      }
    }
  }
`;

// 계좌 단위 리플레이: 입금/출금/매수/매도/평가 이벤트를 시간순으로 재생해
// "원금(외부 순투입액)"과 "자산(평가금액+예수금)"이 어떻게 변해왔는지를 계산한다.
// 매수/매도는 예수금<->주식 내부 이동이라 원금과 자산 어느 쪽도 바꾸지 않으므로
// 부분 매도를 아무리 해도 수익률이 꼬이지 않는다. 백엔드 domain.py의
// _replay_investment_events와 같은 규칙(같은 날짜 안에서는 입출금 -> 매수/매도 -> 평가 순).
function computeInvestmentTimeline(valuations) {
  const priority = { deposit: 0, withdraw: 0, buy: 1, sell: 1, valuation: 2 };
  const sorted = [...valuations].sort((a, b) => {
    const dayA = String(a.evaluation_date).slice(0, 10);
    const dayB = String(b.evaluation_date).slice(0, 10);
    if (dayA !== dayB) return dayA < dayB ? -1 : 1;
    return (priority[a.transaction_type] ?? 1) - (priority[b.transaction_type] ?? 1);
  });
  if (sorted.length === 0) return [];

  let principal = 0;
  let cash = 0;
  let evaluated = 0;

  return sorted.map((v) => {
    const amt = v.evaluated_amount;
    if (v.transaction_type === 'deposit') {
      cash += amt;
      principal += amt;
    } else if (v.transaction_type === 'withdraw') {
      cash -= amt;
      principal -= amt;
    } else if (v.transaction_type === 'buy') {
      if (amt > cash) {
        principal += amt - cash; // 입금 기록 없이 매수한 경우: 부족분을 원금 투입으로 간주
        cash = 0;
      } else {
        cash -= amt;
      }
      evaluated += amt;
    } else if (v.transaction_type === 'sell') {
      cash += amt;
      evaluated = Math.max(evaluated - amt, 0);
    } else if (v.transaction_type === 'valuation') {
      evaluated = amt;
    }

    const asset = evaluated + cash;
    return {
      date: v.evaluation_date,
      eventType: v.transaction_type,
      principal,
      cash,
      evaluated,
      asset,
      returnRate: principal > 0 ? ((asset - principal) / principal) * 100 : 0,
    };
  });
}

function AccountDetail() {
  const { id } = useParams();
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' or 'chart'
  const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] = useState(false);
  const [isEditTransactionModalOpen, setIsEditTransactionModalOpen] = useState(false);
  const [isEditValuationModalOpen, setIsEditValuationModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedValuation, setSelectedValuation] = useState(null);
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);
  const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] = useState(false);
  const [isDeactivateAccountDialogOpen, setIsDeactivateAccountDialogOpen] = useState(false);

  useEffect(() => {
    fetchAccountDetails();
  }, [id]);

  const fetchAccountDetails = async () => {
    try {
      const [accountResponse, transactionsResponse] = await Promise.all([
        accountApi.getAccount(id),
        accountApi.getTransactions(id)
      ]);

      const accountData = accountResponse.data;
      setAccount(accountData);
      setTransactions(transactionsResponse.data);
      
      // Fetch valuations if it's an investment account
      if (accountData.type === '투자') {
        try {
          const valuationsResponse = await accountApi.getValuations(id);
          setValuations(valuationsResponse.data || []);
        } catch (error) {
          console.error('Error fetching valuations:', error);
          setValuations([]);
        }
      } else {
        setValuations([]);
      }
    } catch (error) {
      console.error('Error fetching account details:', error);
    }
  };

  const calculateBalance = (account) => {
    if (account.type === '투자') {
      // For investment accounts, use asset_value if available
      return account.asset_value || 0;
    }

    // For other account types, calculate balance from transactions
    const income = account.transactions
      ? account.transactions
        .filter(t => t.type === "income")
        .reduce((sum, t) => sum + t.amount, 0)
      : 0;

    const expense = account.transactions
      ? account.transactions
        .filter(t => t.type === "expense")
        .reduce((sum, t) => sum + t.amount, 0)
      : 0;

    return account.opening_balance + income - expense;
  };

  const handleAddTransaction = () => {
    setIsAddTransactionModalOpen(true);
  };

  const handleTransactionAdded = () => {
    // Refresh the transaction list
    fetchAccountDetails();
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsEditTransactionModalOpen(true);
  };

  const handleTransactionUpdated = () => {
    fetchAccountDetails();
    setIsEditTransactionModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleValuationClick = (valuation) => {
    setSelectedValuation(valuation);
    setIsEditValuationModalOpen(true);
  };

  const handleValuationUpdated = () => {
    fetchAccountDetails();
    setIsEditValuationModalOpen(false);
    setSelectedValuation(null);
  };

  const handleEditAccount = () => {
    setIsEditAccountModalOpen(true);
  };

  const handleAccountUpdated = () => {
    // Refresh the account details
    fetchAccountDetails();
  };

  const handleDeleteAccount = () => {
    setIsDeleteAccountDialogOpen(true);
  };

  const handleDeleteAccountConfirm = async () => {
    try {
      // In a real implementation, we would call the API to delete the account
      // await accountApi.deleteAccount(id);
      // onClose();
      // window.location.href = '/'; // Navigate back to account list

      // For now, we'll just simulate the API call
      console.log('Deleting account with id:', id);
      alert('계좌가 성공적으로 삭제되었습니다!');
      setIsDeleteAccountDialogOpen(false);
      // In a real implementation, we would navigate back to the account list
      // window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('계좌 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleDeactivateAccount = () => {
    setIsDeactivateAccountDialogOpen(true);
  };

  const handleDeactivateAccountConfirm = async () => {
    try {
      await accountApi.toggleAccountStatus(id);
      setIsDeactivateAccountDialogOpen(false);
      fetchAccountDetails(); // Refresh the account details
    } catch (error) {
      console.error('Error toggling account status:', error);
      const action = account.status === 'dead' ? '활성화' : '비활성화';
      alert(`계좌 ${action} 중 오류가 발생했습니다.`);
    }
  };

  const calculateReturnRateData = () => {
    if (!account || account.type !== '투자' || valuations.length === 0) {
      return null;
    }

    const timeline = computeInvestmentTimeline(valuations);
    if (timeline.length === 0) {
      return null;
    }

    const labels = timeline.map((p) =>
      new Date(p.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
    );

    const principalDataset = {
      label: '원금',
      data: timeline.map((p) => p.principal),
      borderColor: '#6c757d',
      backgroundColor: '#6c757d20',
      borderDash: [5, 5],
      fill: false,
      tension: 0.2,
      pointRadius: 5,
      pointHoverRadius: 8,
      pointBackgroundColor: '#6c757d',
      pointBorderColor: '#6c757d',
    };

    // 자산 줄의 각 점은 그 시점 이벤트 종류로 색을 구분
    // (입금=초록, 출금=빨강, 매수=노랑, 매도=계좌색, 평가=회색)
    const eventColor = (eventType) => {
      if (eventType === 'deposit') return '#28a745';
      if (eventType === 'withdraw') return '#dc3545';
      if (eventType === 'buy') return '#ffc107';
      if (eventType === 'sell') return account.color || '#007bff';
      return '#6c757d';
    };

    const assetDataset = {
      label: '자산 (평가금액+예수금)',
      data: timeline.map((p) => p.asset),
      borderColor: account.color || '#007bff',
      backgroundColor: `${account.color || '#007bff'}20`,
      fill: false,
      tension: 0.2,
      pointRadius: 5,
      pointHoverRadius: 8,
      pointBackgroundColor: (ctx) => eventColor(timeline[ctx.dataIndex]?.eventType),
      pointBorderColor: (ctx) => eventColor(timeline[ctx.dataIndex]?.eventType),
    };

    return {
      labels,
      datasets: [principalDataset, assetDataset],
      timeline,
    };
  };

  const chartData = calculateReturnRateData();

  // 투자 계좌 요약 - 차트와 같은 리플레이의 마지막 시점 값을 그대로 사용 (단일 소스)
  // 수익률 = (자산 - 원금) / 원금. 매수/매도는 내부 이동이라 수익률에 영향 없음.
  const calculateInvestmentInfo = () => {
    if (!account) return { asset: 0, principal: 0, returnRate: 0 };

    const timeline = computeInvestmentTimeline(valuations);
    if (timeline.length === 0) {
      // 기록이 없으면 백엔드가 내려준 실제 필드로 자산만 표시
      return {
        asset: (account.evaluated_amount || 0) + (account.cash_balance || 0),
        principal: 0,
        returnRate: 0,
      };
    }

    const last = timeline[timeline.length - 1];
    return { asset: last.asset, principal: last.principal, returnRate: last.returnRate };
  };

  const investmentInfo = account?.type === '투자' ? calculateInvestmentInfo() : null;

  if (!account) {
    return <div>Loading...</div>;
  }

  const isInvestmentAccount = account.type === '투자';

  return (
    <AccountDetailContainer>
      <div className="account-header">

        <div className="header-top">
          <h2>{account.name} ({account.type})</h2>
          <div className="header-actions">
            <button className="action-button edit" onClick={handleEditAccount}>계좌 수정</button>
            <button className="action-button deactivate" onClick={handleDeactivateAccount}>
              {account.status === 'dead' ? '계좌 활성화' : '계좌 비활성화'}
            </button>
            <button className="action-button delete" onClick={handleDeleteAccount}>계좌 삭제</button>
          </div>
        </div>
        <div className="account-info">
          {account.type === '투자' ? (
            <>
              <div className="info-item">
                <div className="label">자산</div>
                <div className="value">{formatCurrency(investmentInfo?.asset || 0)}</div>
              </div>
              <div className="info-item">
                <div className="label">매입금액</div>
                <div className="value">{formatCurrency(account.purchase_amount)}</div>
              </div>
              <div className="info-item">
                <div className="label">평가금액</div>
                <div className="value">{formatCurrency(account.evaluated_amount)}</div>
              </div>
              <div className="info-item">
                <div className="label">예수금</div>
                <div className="value">{formatCurrency(account.cash_balance || 0)}</div>
              </div>
              <div className="info-item">
                <div className="label">수익률</div>
                <div className="value">{(investmentInfo?.returnRate ?? 0).toFixed(1)}%</div>
              </div>
            </>
          ) : (
            <>
              <div className="info-item">
                <div className="label">잔액</div>
                <div className="value">{formatCurrency(calculateBalance(account))}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {isInvestmentAccount ? (
        <div className="tabs-container">
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              거래 내역
            </button>
            <button
              className={`tab-button ${activeTab === 'chart' ? 'active' : ''}`}
              onClick={() => setActiveTab('chart')}
            >
              수익률 차트
            </button>
          </div>
          <div className="tab-content">
            {activeTab === 'transactions' ? (
              <div className="transactions-table">
                <div className="transactions-header">
                  <h3>거래 내역</h3>
                  <button className="action-button" onClick={handleAddTransaction}>거래 추가</button>
                </div>
                {(transactions.length > 0 || valuations.length > 0) ? (
                  <table className="investment-transactions-table">
                    <thead>
                      <tr>
                        <th>타입</th>
                        <th>금액</th>
                        <th>메모</th>
                        <th>날짜</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        // Combine transactions and valuations, sorted by date
                        ...transactions.map(t => ({ ...t, itemType: 'transaction' })),
                        ...valuations.map(v => ({ ...v, itemType: 'valuation' }))
                      ]
                        .sort((a, b) => {
                          const dateA = a.itemType === 'transaction' ? a.date : a.evaluation_date;
                          const dateB = b.itemType === 'transaction' ? b.date : b.evaluation_date;
                          return new Date(dateB) - new Date(dateA); // 최신순
                        })
                        .map(item => {
                          if (item.itemType === 'transaction') {
                            return (
                              <tr 
                                key={item.id} 
                                className={item.category === '이동' ? 'transfer' : ''}
                                onClick={() => handleTransactionClick(item)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td className={item.type === 'income' ? 'income' : 'expense'}>
                                  {item.type === 'income' ? '수입' : '지출'}
                                </td>
                                <td>{formatCurrency(item.amount)}</td>
                                <td>{item.memo}</td>
                                <td>{item.date.substring(0, 10)}</td>
                              </tr>
                            );
                          } else {
                            // Valuation record
                            const typeLabel = item.transaction_type === 'buy' ? '매수' :
                                            item.transaction_type === 'sell' ? '매도' :
                                            item.transaction_type === 'deposit' ? '입금' :
                                            item.transaction_type === 'withdraw' ? '출금' : '평가';
                            return (
                              <tr 
                                key={item.id} 
                                className="valuation"
                                onClick={() => handleValuationClick(item)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td className="valuation-type">{typeLabel}</td>
                                <td>{formatCurrency(item.evaluated_amount)}</td>
                                <td>{item.memo || '-'}</td>
                                <td>{item.evaluation_date.substring(0, 10)}</td>
                              </tr>
                            );
                          }
                        })}
                    </tbody>
                  </table>
                ) : (
                  <p>거래 내역이 없습니다.</p>
                )}
              </div>
            ) : (
              <div className="chart-container">
                <h3>수익률 추이</h3>
                {chartData && valuations.length > 0 ? (
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top',
                        },
                        datalabels: {
                          display: true,
                          color: '#333',
                          anchor: 'end',
                          align: 'top',
                          formatter: function(value) {
                            // 금액으로 표시
                            return (value / 10000).toFixed(0) + '만원';
                          },
                          font: {
                            size: 11,
                            weight: 'bold'
                          }
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: function(context) {
                              const timeline = chartData.timeline || [];
                              const point = timeline[context.dataIndex];
                              if (!point) return `${context.parsed.y.toLocaleString()}원`;

                              if (context.datasetIndex === 0) {
                                // 원금 줄
                                return `원금: ${point.principal.toLocaleString()}원`;
                              }

                              // 자산 줄
                              const typeLabel = point.eventType === 'buy' ? '매수'
                                : point.eventType === 'sell' ? '매도'
                                : point.eventType === 'deposit' ? '입금'
                                : point.eventType === 'withdraw' ? '출금' : '평가';
                              return [
                                `[${typeLabel}] 자산: ${point.asset.toLocaleString()}원`,
                                `평가금액 ${point.evaluated.toLocaleString()}원 + 예수금 ${point.cash.toLocaleString()}원`,
                                `수익률: ${point.returnRate.toFixed(1)}%`
                              ];
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: false,
                          ticks: {
                            callback: function(value) {
                              // 금액으로 표시 (만원 단위)
                              return (value / 10000).toFixed(0) + '만원';
                            }
                          },
                          title: {
                            display: true,
                            text: '금액 (원)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: '날짜'
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <p>수익률 데이터가 없습니다. 평가 기록을 추가해주세요.</p>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="transactions-table">
          <div className="transactions-header">
            <h3>거래 내역</h3>
            <button className="action-button" onClick={handleAddTransaction}>거래 추가</button>
          </div>
          {transactions.length > 0 ? (
            <table className="cash-transactions-table">
              <thead>
                <tr>
                  <th>타입</th>
                  <th>금액</th>
                  <th>카테고리</th>
                  <th>메모</th>
                  <th>날짜</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(transaction => (
                  <tr 
                    key={transaction.id} 
                    className={transaction.category === '이동' ? 'transfer' : ''}
                    onClick={() => handleTransactionClick(transaction)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className={transaction.type === 'income' ? 'income' : 'expense'}>
                      {transaction.type === 'income' ? '수입' : '지출'}
                    </td>
                    <td>{formatCurrency(transaction.amount)}</td>
                    <td>{transaction.category}</td>
                    <td>{transaction.memo}</td>
                    <td>{transaction.date.substring(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>거래 내역이 없습니다.</p>
          )}
        </div>
      )}

      <AddTransactionModal
        isOpen={isAddTransactionModalOpen}
        onClose={() => setIsAddTransactionModalOpen(false)}
        accountId={id}
        onTransactionAdded={handleTransactionAdded}
        accountType={account.type}
      />

      <EditTransactionModal
        isOpen={isEditTransactionModalOpen}
        onClose={() => {
          setIsEditTransactionModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        accountId={id}
        onTransactionUpdated={handleTransactionUpdated}
        accountType={account.type}
      />

      <EditValuationModal
        isOpen={isEditValuationModalOpen}
        onClose={() => {
          setIsEditValuationModalOpen(false);
          setSelectedValuation(null);
        }}
        valuation={selectedValuation}
        accountId={id}
        onValuationUpdated={handleValuationUpdated}
      />

      <EditAccountModal
        isOpen={isEditAccountModalOpen}
        onClose={() => setIsEditAccountModalOpen(false)}
        account={account}
        onAccountUpdated={handleAccountUpdated}
      />

      <DeleteAccountDialog
        isOpen={isDeleteAccountDialogOpen}
        onClose={() => setIsDeleteAccountDialogOpen(false)}
        onConfirm={handleDeleteAccountConfirm}
        accountName={account.name}
      />

      <DeactivateAccountDialog
        isOpen={isDeactivateAccountDialogOpen}
        onClose={() => setIsDeactivateAccountDialogOpen(false)}
        onConfirm={handleDeactivateAccountConfirm}
        accountName={account.name}
        isActivating={account.status === 'dead'}
      />
    </AccountDetailContainer>
  );
}

export default AccountDetail;
